import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Frame,
  Hourglass,
  NumberInput,
  Radio,
  Table,
  TableBody,
  TableDataCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Toolbar,
  Tooltip,
  Window,
  WindowContent,
  WindowHeader
} from 'react95';
import {
  setBulkEditMode,
  setBulkEditTasks,
  setTaskListOrder,
  setTaskListPageNumber
} from '../../actions/taskListActions';
import {
  ALL_STAGES,
  DEFAULT_STAGES,
  ENG_DEFAULT_STAGES,
  TASKS_PER_PAGE,
  TASK_LIST_COLS
} from '../../global/Environment';
import {
  bulkEditModeSelector,
  bulkEditTasksSelector,
  showAllReturnsSelector,
  taskListCurrentTabSelector,
  taskListErrorMessageSelector,
  taskListFiltersSelector,
  taskListOrderSelector,
  taskListPageNumberSelector,
  yearSelector
} from '../../selectors/taskListSelectors';
import { formatTableData, getStageOptions, getTaskFieldDisplayVal, isCurrentUserEng } from '../../utils/taskUtils';
import './TaskListTable.scss';
import { useFetchQDeckUsersQuery, useQDeckUserEmailNameMap } from '../../api/qdeckApi';
import { TASK_LIST_TABS } from '../../constants';
import { useGetTasksQuery, useGetTaskStageCountsQuery, useGetTaskSubstageCountsQuery } from '../../api/opsSystemApi';
import { userSelector } from '../../selectors/authSelectors';
import { isEmpty, sumBy } from 'lodash-es';

const TaskListTable = ({ isAdmin, tasks, areTasksLoading }) => {
  const dispatch = useDispatch();

  const year = useSelector(yearSelector);
  const showAllReturns = useSelector(showAllReturnsSelector);
  const errorMessage = useSelector(taskListErrorMessageSelector);
  const taskListOrder = useSelector(taskListOrderSelector);
  const bulkEditMode = useSelector(bulkEditModeSelector);
  const bulkEditTasks = useSelector(bulkEditTasksSelector);
  const filters = useSelector(taskListFiltersSelector);
  const currentTab = useSelector(taskListCurrentTabSelector);
  const pageNumber = useSelector(taskListPageNumberSelector);
  const user = useSelector(userSelector);
  const order = useSelector(taskListOrderSelector);

  const [pageInputValue, setPageInputValue] = useState(pageNumber);

  const { qdeckUserEmailNameMap, isLoading: isQDeckUserEmailNameMapLoading } = useQDeckUserEmailNameMap();
  const { data: qdeckUsers = [], isLoading: areQDeckUsersLoading } = useFetchQDeckUsersQuery();

  const isSubstageView = currentTab === TASK_LIST_TABS.SUBSTAGE_TASKS;
  const isEng = isCurrentUserEng(user.email, qdeckUsers);
  const stages = getStageOptions(showAllReturns, isEng);
  const { data: stageCounts, isLoading: areStageCountsLoading } = useGetTaskStageCountsQuery(
    {
      taxYear: year,
      showAllReturns,
      filters
    },
    {
      skip: isSubstageView
    }
  );
  const { data: substageCounts, isLoading: areSubstageCountsLoading } = useGetTaskSubstageCountsQuery(
    {
      taxYear: year,
      stages,
      showAllReturns,
      filters
    },
    {
      skip: !isSubstageView || areQDeckUsersLoading
    }
  );
  const {
    data: allTasks = [],
    isLoading: areAllTasksLoading,
    isFetching: areAllTasksFetching
  } = useGetTasksQuery(
    {
      year,
      showAllReturns,
      order,
      filters
    },
    {
      skip: areQDeckUsersLoading
    }
  );

  const isLoading =
    areTasksLoading || isQDeckUserEmailNameMapLoading || areStageCountsLoading || areSubstageCountsLoading;

  const getActiveStageFilters = useCallback(() => {
    if (!isEmpty(filters.stages)) {
      return filters.stages;
    } else if (!showAllReturns) {
      return isEng ? ENG_DEFAULT_STAGES : DEFAULT_STAGES;
    }
    return ALL_STAGES;
  }, [filters.stages, showAllReturns, isEng]);

  /** Total number of pages for the current filters */
  const numPages = useMemo(() => {
    if (isLoading) {
      return 1;
    }
    if (isSubstageView) {
      const totalTasks = sumBy(Object.entries(substageCounts), ([substage, value]) =>
        substage === filters.substage ? Number(value) : 0
      );
      return Math.ceil(totalTasks / TASKS_PER_PAGE);
    }
    const activeStageFilters = getActiveStageFilters();
    const totalTasks = sumBy(Object.entries(stageCounts), ([stage, value]) =>
      activeStageFilters.includes(stage) ? Number(value) : 0
    );
    return Math.ceil(totalTasks / TASKS_PER_PAGE);
  }, [isSubstageView, substageCounts, stageCounts, isLoading, getActiveStageFilters, filters.substage]);

  const listTitle = filters.assignee
    ? `Task List ${year} - Assigned to me`
    : currentTab === TASK_LIST_TABS.SUBSTAGE_TASKS
      ? `Task List ${year} - Substage tasks`
      : `Task List ${year}`;
  const title = `${listTitle} - ${showAllReturns ? 'SHOWING ALL RETURNS' : 'ACTIONABLE RETURNS ONLY'}`;

  const updateOrder = async (orderCol) => {
    if (!orderCol) {
      dispatch(setTaskListOrder(null));
    } else if (taskListOrder && taskListOrder[0] === orderCol) {
      dispatch(setTaskListOrder([orderCol, taskListOrder[1] === 'ASC' ? 'DESC' : 'ASC']));
    } else if (orderCol === 'updatedAt') {
      // The default order on initial load is by updatedAt ASC,
      // whereas all other columns are by default DESC
      dispatch(setTaskListOrder([orderCol, 'DESC']));
    } else {
      dispatch(setTaskListOrder([orderCol, 'ASC']));
    }
  };

  // Only admins can select and update multiple tasks;
  // All other users can select one task at a time
  const selectTask = (task, selected) => {
    const { id } = task;

    if (!isAdmin) {
      dispatch(setBulkEditTasks(selected ? [id] : []));
      return;
    }

    if (selected) {
      dispatch(setBulkEditTasks([...bulkEditTasks, id]));
    } else {
      dispatch(setBulkEditTasks(bulkEditTasks.filter((taskId) => taskId !== id)));
    }
  };

  /** Shared function to update the page number */
  const updatePageNumber = useCallback(
    (value) => {
      const inputValue = Number(value);
      if (!isNaN(inputValue) && inputValue >= 1 && inputValue <= numPages) {
        dispatch(setTaskListPageNumber(value));
      }
      setPageInputValue(value);
    },
    [dispatch, numPages]
  );

  /** Called whenever the page number is updated via the arrow buttons */
  const handlePageUpdate = useCallback(
    (updatedPage) => {
      updatePageNumber(updatedPage);
    },
    [updatePageNumber]
  );

  /** Called when the user types in the input field */
  const handlePageInput = useCallback(
    (e) => {
      updatePageNumber(e.target.value);
    },
    [updatePageNumber]
  );

  // Ensure the page number is not greater than the number of pages
  useEffect(() => {
    if (pageNumber > numPages) {
      updatePageNumber(1);
    }
  }, [pageNumber, numPages, updatePageNumber]);

  return (
    <Window className='task-list-table-window'>
      <div className='task-list-table-window-inner'>
        <div className='task-list-table-window-inner-top'>
          <WindowHeader className='task-list-table-header'>
            <Tooltip className='task-list-table-tooltip' text='Default order' enterDelay={0} position='right'>
              <Button
                square
                size='sm'
                className={classNames('task-list-table-tooltip-btn', {
                  disabled: !taskListOrder
                })}
                disabled={!taskListOrder}
                onClick={() => updateOrder(null)}
              >
                <div className='task-list-table-tooltip-btn-icon'>X</div>
              </Button>
            </Tooltip>
            <div className='task-list-table-title'>{title}</div>
            {isLoading && <Hourglass />}
          </WindowHeader>
          <WindowContent className='task-list-table-content'>
            {tasks.length ? (
              <div className='task-list-table-container'>
                <div className='task-list-table-container-inner'>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeadCell className='task-list-col-sp' key='actions'>
                          <span>Actions</span>
                        </TableHeadCell>

                        {TASK_LIST_COLS.map((colName) => {
                          return (
                            <TableHeadCell key={colName} onClick={() => updateOrder(colName)}>
                              {colName}
                            </TableHeadCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableDataCell className='task-list-col-sp' key={task.id + 'open'}>
                            <div>
                              {bulkEditMode === 'select' && (
                                <>
                                  {isAdmin ? (
                                    <Checkbox
                                      checked={bulkEditTasks.includes(task.id)}
                                      onChange={(e) => selectTask(task, e.target.checked)}
                                      disabled={isLoading}
                                    />
                                  ) : (
                                    <Radio
                                      checked={bulkEditTasks.includes(task.id)}
                                      disabled={isLoading}
                                      onChange={(e) => {
                                        selectTask(task, e.target.checked);
                                        dispatch(setBulkEditMode('selected'));
                                      }}
                                    />
                                  )}
                                </>
                              )}
                              <Link to={`/task-detail/${task.id}`} target='_blank'>
                                <Button primary variant='flat' size='sm'>
                                  Open
                                </Button>
                              </Link>
                            </div>
                          </TableDataCell>

                          {TASK_LIST_COLS.map((colName) => {
                            return (
                              <TableDataCell key={task.id + colName}>
                                <div>
                                  {getTaskFieldDisplayVal(colName, task, qdeckUserEmailNameMap, filters.search)}
                                </div>
                              </TableDataCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div>No tasks found {':('}</div>
            )}
          </WindowContent>
        </div>

        <div className='task-list-table-window-inner-bottom'>
          <Frame variant='status'>
            <Toolbar className='task-list-table-footer'>
              <div className='task-list-table-footer-pages'>
                <span>Page</span>
                <NumberInput
                  min={1}
                  max={numPages}
                  value={pageInputValue}
                  onChange={handlePageUpdate}
                  onInput={handlePageInput}
                />
                <span>of {numPages}</span>
              </div>
              <div className='task-list-table-footer-res'>
                {errorMessage && <span>{errorMessage}</span>}
                {!(areAllTasksFetching || areAllTasksLoading) && (
                  <CSVLink data={formatTableData(allTasks)} filename='taskList.csv'>
                    Download CSV
                  </CSVLink>
                )}
              </div>
            </Toolbar>
          </Frame>
        </div>
      </div>
    </Window>
  );
};

export default TaskListTable;
